import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { FilterSelect } from "@/components/data/filter-select";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { LiveDot } from "@/components/display/live-dot";
import { EmptyState } from "@/components/feedback/states";
import { type Execution, useExecutionsPage } from "@/api/executions";
import { usePipelineNames } from "@/api/pipelines";
import { useLiveInvalidate } from "@/lib/live";
import { PULSE_SX, usePulse } from "@/stores/live-runs";
import { useTableParams } from "@/lib/table-params";
import { fmtDuration, fmtNumber } from "@/lib/format";

const STATUS_OPTIONS = ["RUNNING", "SUCCESS", "FAILED", "STALE", "QUEUED"] as const;
const TRIGGER_OPTIONS = ["MANUAL", "SCHEDULED", "API", "EVENT", "RETRY"] as const;

export const Route = createFileRoute("/_app/executions/")({
  component: ExecutionsList,
});

function ExecutionsList() {
  const navigate = useNavigate();
  const params = useTableParams({ sort: { id: "createdAt", dir: "desc" } });
  const runs = useExecutionsPage(params);
  const names = usePipelineNames();
  const sse = useLiveInvalidate([["executions", "page"]]);
  const pulse = usePulse();

  const pipelineOptions = useMemo(
    () =>
      Object.entries(names)
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [names],
  );

  const fromDate = (params.filters.from ?? "").slice(0, 10);
  const toDate = (params.filters.to ?? "").slice(0, 10);
  const failedOnly = params.filters.status === "FAILED";

  const columns: Column<Execution>[] = [
    {
      id: "pipeline",
      header: "Pipeline",
      cell: (r) => (r.pipelineId ? (names[r.pipelineId] ?? r.pipelineId) : "—"),
    },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
    { id: "trigger", header: "Trigger", cell: (r) => r.triggerType ?? "—", nowrap: true },
    {
      id: "recordsWritten",
      header: "Written",
      align: "right",
      cell: (r) => fmtNumber(r.recordsWritten),
      sortValue: (r) => r.recordsWritten,
    },
    { id: "failed", header: "Failed", align: "right", cell: (r) => fmtNumber(r.recordsFailed) },
    {
      id: "durationMs",
      header: "Duration",
      align: "right",
      cell: (r) => fmtDuration(r.durationMs),
      sortValue: (r) => r.durationMs,
    },
    {
      id: "createdAt",
      header: "Started",
      cell: (r) => <RelativeTime value={r.startedAt} />,
      sortValue: (r) => r.startedAt,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Executions"
        description="Every pipeline run across the tenant."
        actions={<LiveDot status={sse === "open" ? "online" : "connecting"} label="Live" />}
      />
      <Card>
        <DataTable
          columns={columns}
          data={runs.data?.content}
          total={runs.data?.totalElements}
          params={params}
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={() => runs.refetch()}
          rowKey={(r) => r.id}
          rowSx={(r) => (pulse(r.id) ? PULSE_SX : undefined)}
          hideSearch
          filters={
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1, alignItems: "center" }}
            >
              <FilterSelect
                label="Pipeline"
                value={params.filters.pipelineId ?? ""}
                onChange={(v) => params.setFilter("pipelineId", v)}
                options={pipelineOptions}
                minWidth={170}
              />
              <FilterSelect
                label="Status"
                value={params.filters.status ?? ""}
                onChange={(v) => params.setFilter("status", v)}
                options={[...STATUS_OPTIONS]}
              />
              <FilterSelect
                label="Trigger"
                value={params.filters.triggerType ?? ""}
                onChange={(v) => params.setFilter("triggerType", v)}
                options={[...TRIGGER_OPTIONS]}
              />
              <DatePicker
                label="From"
                value={fromDate ? dayjs(fromDate) : null}
                onChange={(d) =>
                  params.setFilter("from", d ? `${d.format("YYYY-MM-DD")}T00:00:00Z` : "")
                }
                maxDate={toDate ? dayjs(toDate) : undefined}
                slotProps={{
                  textField: { size: "small", sx: { width: 160 } },
                  field: { clearable: true },
                }}
              />
              <DatePicker
                label="To"
                value={toDate ? dayjs(toDate) : null}
                onChange={(d) =>
                  params.setFilter("to", d ? `${d.format("YYYY-MM-DD")}T23:59:59Z` : "")
                }
                minDate={fromDate ? dayjs(fromDate) : undefined}
                disableFuture
                slotProps={{
                  textField: { size: "small", sx: { width: 160 } },
                  field: { clearable: true },
                }}
              />
              <Chip
                size="small"
                label="Failed only"
                color={failedOnly ? "error" : "default"}
                variant={failedOnly ? "filled" : "outlined"}
                onClick={() => params.setFilter("status", failedOnly ? "" : "FAILED")}
              />
            </Stack>
          }
          onRowClick={(r) =>
            r.pipelineId
              ? navigate({
                  to: "/pipelines/$pipelineId/runs/$runId",
                  params: { pipelineId: r.pipelineId, runId: r.id },
                })
              : undefined
          }
          empty={<EmptyState title="No runs" description="No executions match these filters." />}
        />
      </Card>
    </PageContainer>
  );
}
